import { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { 
  Users, 
  Calculator, 
  TrendingUp, 
  ShieldCheck,
  Building2,
  Globe,
  Handshake,
  Target,
  PieChart
} from "lucide-react";
import Logo from "@/components/Logo";

interface ServiceItem {
  id: number;
  title: string;
  shortTitle: string;
  description: string;
  icon: React.ElementType;
}

const services: ServiceItem[] = [
  {
    id: 1,
    title: "Planejamento Sucessório",
    shortTitle: "Sucessão",
    description: "Estruturação sucessória preventiva para reduzir riscos e conflitos.",
    icon: Users,
  },
  {
    id: 2,
    title: "Abertura de Holding",
    shortTitle: "Holding",
    description: "Estruturação de holdings para proteção e organização patrimonial.",
    icon: Building2,
  },
  {
    id: 3,
    title: "Offshore e Trusts",
    shortTitle: "Offshore",
    description: "Estruturas internacionais para diversificação e proteção de ativos.",
    icon: Globe,
  },
  {
    id: 4,
    title: "Otimização Tributária",
    shortTitle: "Tributário",
    description: "Estruturas tributárias mais eficientes, sempre dentro da legalidade.",
    icon: Calculator,
  },
  {
    id: 5,
    title: "Investimentos",
    shortTitle: "Investimentos",
    description: "Análise e acompanhamento de investimentos alinhados aos seus objetivos.",
    icon: PieChart,
  },
  {
    id: 6,
    title: "Planejamento",
    shortTitle: "Planejamento",
    description: "Planejamento financeiro completo orientado por objetivos de vida.",
    icon: Target,
  },
  {
    id: 7,
    title: "Advisory Financeiro",
    shortTitle: "Advisory",
    description: "Apoio em decisões financeiras relevantes e revisões de estratégia.",
    icon: TrendingUp,
  },
  {
    id: 8,
    title: "Curadoria de Seguros",
    shortTitle: "Seguros",
    description: "Avaliação e escolha de seguros alinhados às suas necessidades.",
    icon: ShieldCheck,
  },
  {
    id: 9,
    title: "Alavancagem com Consórcios",
    shortTitle: "Consórcios",
    description: "Estratégias de alavancagem patrimonial através de consórcios.",
    icon: Handshake,
  },
];

export default function ServicesOrbit() {
  const [rotationAngle, setRotationAngle] = useState(0);
  const [activeService, setActiveService] = useState<ServiceItem | null>(null);
  const [autoRotate, setAutoRotate] = useState(true);
  const [isMobile, setIsMobile] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    let rotationTimer: NodeJS.Timeout;

    if (autoRotate) {
      rotationTimer = setInterval(() => {
        setRotationAngle((prev) => {
          const newAngle = (prev + 0.15) % 360;
          return Number(newAngle.toFixed(3));
        });
      }, 50);
    }

    return () => {
      if (rotationTimer) {
        clearInterval(rotationTimer);
      }
    };
  }, [autoRotate]);

  const calculateNodePosition = (index: number, total: number) => {
    const angle = ((index / total) * 360 + rotationAngle) % 360;
    const radius = isMobile ? 120 : 180;
    const radian = (angle * Math.PI) / 180;

    const x = radius * Math.cos(radian);
    const y = radius * Math.sin(radian);

    const zIndex = Math.round(100 + 50 * Math.cos(radian));
    const scale = 0.7 + 0.3 * ((1 + Math.cos(radian)) / 2);
    const opacity = 0.4 + 0.6 * ((1 + Math.cos(radian)) / 2);

    return { x, y, angle, zIndex, scale, opacity };
  };

  const handleServiceClick = (service: ServiceItem) => {
    if (activeService?.id === service.id) {
      setActiveService(null);
      setAutoRotate(true);
    } else {
      setActiveService(service);
      setAutoRotate(false);
      
      // Rotate to center the clicked service
      const index = services.findIndex(s => s.id === service.id);
      const targetAngle = (index / services.length) * 360;
      setRotationAngle(180 - targetAngle);
    }
  };

  const handleContainerClick = (e: React.MouseEvent) => {
    if (e.target === containerRef.current) {
      setActiveService(null);
      setAutoRotate(true);
    }
  };

  return (
    <div
      ref={containerRef}
      className="relative w-full h-[400px] md:h-[500px] flex items-center justify-center"
      onClick={handleContainerClick}
    >
      {/* Orbital rings - more visible gold */}
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
        <motion.div 
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1, delay: 0.2 }}
          className="absolute w-[280px] h-[280px] md:w-[400px] md:h-[400px] rounded-full"
          style={{ border: '1px solid hsl(43 65% 45% / 0.25)' }}
        />
        <motion.div 
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1, delay: 0.3 }}
          className="absolute w-[200px] h-[200px] md:w-[300px] md:h-[300px] rounded-full"
          style={{ border: '1px solid hsl(43 65% 45% / 0.35)' }}
        />
        <motion.div 
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1, delay: 0.4 }}
          className="absolute w-[120px] h-[120px] md:w-[180px] md:h-[180px] rounded-full"
          style={{ border: '1px solid hsl(43 65% 45% / 0.5)' }}
        />
        
        {/* Gold glow effect */}
        <div 
          className="absolute w-32 h-32 md:w-48 md:h-48 rounded-full blur-3xl"
          style={{ backgroundColor: 'hsl(43 65% 45% / 0.15)' }}
        />
      </div>

      {/* Center - Logo */}
      <motion.div
        initial={{ opacity: 0, scale: 0 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.6, type: "spring" }}
        className="absolute z-50 flex flex-col items-center justify-center"
      >
        <div 
          className="w-20 h-20 md:w-24 md:h-24 rounded-full bg-background flex items-center justify-center p-3"
          style={{ 
            border: '2px solid hsl(43 65% 45% / 0.4)',
            boxShadow: '0 0 30px hsl(43 65% 45% / 0.25)'
          }}
        >
          <Logo size="md" />
        </div>
      </motion.div>

      {/* Service nodes */}
      {services.map((service, index) => {
        const position = calculateNodePosition(index, services.length);
        const isActive = activeService?.id === service.id;
        const Icon = service.icon;

        return (
          <motion.div
            key={service.id}
            initial={{ opacity: 0, scale: 0 }}
            animate={{ 
              opacity: position.opacity,
              scale: isActive ? 1.2 : position.scale,
              x: position.x,
              y: position.y,
            }}
            transition={{ 
              duration: 0.5,
              type: "spring",
              stiffness: 100,
            }}
            className="absolute cursor-pointer"
            style={{ zIndex: isActive ? 200 : position.zIndex }}
            onClick={(e) => {
              e.stopPropagation();
              handleServiceClick(service);
            }}
          >
            {/* Node */}
            <div
              className={`
                relative w-12 h-12 md:w-14 md:h-14 rounded-full flex items-center justify-center
                transition-all duration-300 border-2
                ${isActive 
                  ? "border-primary scale-110" 
                  : "border-primary/50 hover:border-primary"
                }
              `}
              style={{ 
                backgroundColor: isActive ? 'hsl(43 65% 45%)' : 'hsl(var(--background) / 0.9)',
                color: isActive ? 'hsl(var(--cta-foreground))' : 'hsl(43 65% 45%)',
                boxShadow: isActive ? '0 0 20px hsl(43 65% 45% / 0.4)' : '0 0 10px hsl(43 65% 45% / 0.2)'
              }}
            >
              <Icon className="w-5 h-5 md:w-6 md:h-6" />
              
              {/* Pulse effect when active */}
              {isActive && (
                <div className="absolute inset-0 rounded-full bg-primary/30 animate-ping" />
              )}
            </div>

            {/* Label */}
            <div className={`
              absolute top-full left-1/2 -translate-x-1/2 mt-2 whitespace-nowrap
              text-xs md:text-sm font-medium transition-colors duration-300
              ${isActive ? "text-primary" : "text-muted-foreground"}
            `}>
              {service.shortTitle}
            </div>
          </motion.div>
        );
      })}

      {/* Active service info card */}
      {activeService && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 20 }}
          className="absolute -bottom-24 md:-bottom-20 left-1/2 -translate-x-1/2 w-[90%] max-w-md"
        >
          <div className="surface-card p-4 md:p-6 backdrop-blur-lg">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center text-primary flex-shrink-0">
                <activeService.icon className="w-5 h-5" />
              </div>
              <div>
                <h3 className="font-sans font-semibold text-foreground mb-1">
                  {activeService.title}
                </h3>
                <p className="text-sm text-muted-foreground">
                  {activeService.description}
                </p>
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </div>
  );
}
